import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { Scale, HandCoins, Wallet, Users, Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import DataTable from '../components/DataTable';
import StatCard, { StatCardsRow } from '../components/balances/StatCard';
import BalancesFilterBar from '../components/balances/BalancesFilterBar';
import BulkPaymentModal from '../components/balances/BulkPaymentModal';
import PartyBalanceDetail from '../components/balances/PartyBalanceDetail';
import { handleApiError } from '../utils/errorHandler';
import {
  balancesApi,
  AgentBalanceRow,
  AgentBalanceSummary,
  PartyBalanceDetail as PartyDetail,
  ClientOption,
  formatCurrency,
} from '../services/agentBalancesService';

const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  direction: rtl;
  background: white;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PageSubtitle = styled.p`
  margin: 6px 0 0;
  color: var(--text-secondary);
  font-size: 14px;
`;

const TypeBadge = styled.span<{ $agent: boolean }>`
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
  color: #fff;
  background: ${p => (p.$agent ? 'var(--primary-color)' : '#8b5cf6')};
`;

const SettledPill = styled.span`
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
  background: rgba(0, 202, 114, 0.15);
  color: #0a8f52;
`;

const OpenRemaining = styled.span`
  font-weight: 700;
  direction: ltr;
  color: var(--error-color);
`;

const ZeroCount = styled.span`
  color: var(--text-tertiary);
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid var(--primary-color);
  border-radius: var(--border-radius-sm);
  background: var(--primary-color);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: var(--primary-hover);
  }
`;

const NoAccess = styled.p`
  color: var(--text-secondary);
`;

const PAGE_SIZE = 20;

const AgentBalancesPage: React.FC = () => {
  const { canViewFinancialReports } = useAuth();

  // filters
  const [partyType, setPartyType] = useState<number | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [clientId, setClientId] = useState<number | null>(null);
  const [openOnly, setOpenOnly] = useState(true);
  const [page, setPage] = useState(1);

  // data
  const [summary, setSummary] = useState<AgentBalanceSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<ClientOption[]>([]);

  // view
  const [selectedParty, setSelectedParty] = useState<{ partyType: number; sourceId: number } | null>(null);

  // bulk-from-summary
  const [bulkParty, setBulkParty] = useState<PartyDetail | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  // search debounce (~300ms)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const loadSummary = useCallback(async () => {
    try {
      setLoading(true);
      const result = await balancesApi.getSummary({
        page,
        pageSize: PAGE_SIZE,
        partyType: partyType ?? undefined,
        searchTerm: debouncedSearch || undefined,
        clientId: clientId ?? undefined,
        openOnly,
      });
      setSummary(result);
    } catch (err) {
      toast.error(handleApiError(err, 'שגיאה בטעינת יתרות הסוכנים'));
    } finally {
      setLoading(false);
    }
  }, [page, partyType, debouncedSearch, clientId, openOnly]);

  useEffect(() => {
    if (canViewFinancialReports) loadSummary();
  }, [loadSummary, canViewFinancialReports]);

  const loadClients = useCallback(async () => {
    try {
      const data = await balancesApi.getAssignedClients();
      setClients(data);
    } catch {
      // non-blocking — filter typeahead simply stays empty
    }
  }, []);

  useEffect(() => {
    if (canViewFinancialReports) loadClients();
  }, [loadClients, canViewFinancialReports]);

  const resetToPageOne = () => setPage(1);

  const handleClear = () => {
    setPartyType(null);
    setSearchInput('');
    setDebouncedSearch('');
    setClientId(null);
    setOpenOnly(true);
    setPage(1);
  };

  const hasActiveFilters =
    partyType !== null || searchInput !== '' || clientId !== null || openOnly !== true;

  const openBulkFromSummary = async (row: AgentBalanceRow) => {
    try {
      const detail = await balancesApi.getPartyDetail(row.partyType, row.sourceId);
      setBulkParty(detail);
      setBulkOpen(true);
    } catch (err) {
      toast.error(handleApiError(err, 'שגיאה בטעינת פרטי הצד'));
    }
  };

  const columns = [
    { key: 'sourceName', title: 'שם', render: (v: string) => <strong>{v}</strong> },
    {
      key: 'partyType',
      title: 'סוג',
      render: (v: number) => <TypeBadge $agent={v === 1}>{v === 1 ? 'סוכן' : 'ארגון'}</TypeBadge>,
    },
    { key: 'signedClientCount', title: 'לקוחות שחתמו' },
    { key: 'approvedClientCount', title: 'מתוכם אושרו' },
    {
      key: 'pendingLeadCount',
      title: 'לידים ממתינים',
      render: (v: number) => (v > 0 ? v : <ZeroCount>0</ZeroCount>),
    },
    { key: 'totalOwed', title: 'סה"כ חוב', render: (v: number) => formatCurrency(v) },
    { key: 'totalPaid', title: 'שולם', render: (v: number) => formatCurrency(v) },
    {
      key: 'remaining',
      title: 'יתרה',
      render: (v: number, r: AgentBalanceRow) =>
        r.isSettled ? <SettledPill>שולם במלואו</SettledPill> : <OpenRemaining>{formatCurrency(v)}</OpenRemaining>,
    },
    {
      key: 'actions',
      title: 'פעולה',
      render: (_v: any, r: AgentBalanceRow) => (
        <AddButton
          type="button"
          onClick={e => {
            e.stopPropagation();
            openBulkFromSummary(r);
          }}
        >
          <Plus size={14} /> הוסף תשלום
        </AddButton>
      ),
    },
  ];

  if (!canViewFinancialReports) {
    return (
      <PageContainer>
        <NoAccess>אין לך הרשאה לצפות בתוכן זה.</NoAccess>
      </PageContainer>
    );
  }

  // ----- Detail view -----
  if (selectedParty) {
    return (
      <PageContainer>
        <PartyBalanceDetail
          partyType={selectedParty.partyType}
          sourceId={selectedParty.sourceId}
          onBack={() => {
            setSelectedParty(null);
            loadSummary();
          }}
          onDataChanged={loadSummary}
        />
      </PageContainer>
    );
  }

  // ----- Summary view -----
  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>
          <Scale size={28} />
          יתרות סוכנים וארגונים
        </PageTitle>
        <PageSubtitle>למי חייבים עמלות, כמה שולם וכמה נותר — ותשלום מהיר, בודד או מרוכז. כרטיסי הסיכום מתייחסים לכל הסוכנים והארגונים, ללא תלות בסינון הטבלה.</PageSubtitle>
      </PageHeader>

      <StatCardsRow>
        <StatCard
          title="סה&quot;כ יתרה"
          value={formatCurrency(summary?.grandTotalRemaining ?? 0)}
          color={(summary?.grandTotalRemaining ?? 0) > 0.001 ? 'var(--error-color)' : '#0a8f52'}
          icon={<Scale size={20} />}
        />
        <StatCard title="סה&quot;כ חוב" value={formatCurrency(summary?.grandTotalOwed ?? 0)} color="var(--primary-color)" icon={<HandCoins size={20} />} />
        <StatCard title="סה&quot;כ שולם" value={formatCurrency(summary?.grandTotalPaid ?? 0)} color="#0a8f52" icon={<Wallet size={20} />} />
        <StatCard title="צדדים עם יתרה פתוחה" value={summary?.openPartiesCount ?? 0} color="#d97706" icon={<Users size={20} />} />
      </StatCardsRow>

      <BalancesFilterBar
        partyType={partyType}
        onPartyTypeChange={v => { setPartyType(v); resetToPageOne(); }}
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        clientId={clientId}
        onClientIdChange={v => { setClientId(v); resetToPageOne(); }}
        clients={clients}
        openOnly={openOnly}
        onOpenOnlyChange={v => { setOpenOnly(v); resetToPageOne(); }}
        onClear={handleClear}
        hasActiveFilters={hasActiveFilters}
      />

      <DataTable<AgentBalanceRow>
        data={summary?.items ?? []}
        columns={columns}
        loading={loading}
        onRowClick={r => setSelectedParty({ partyType: r.partyType, sourceId: r.sourceId })}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: summary?.totalCount ?? 0,
          onChange: newPage => setPage(newPage),
        }}
      />

      {bulkParty && (
        <BulkPaymentModal
          isOpen={bulkOpen}
          onClose={() => setBulkOpen(false)}
          party={bulkParty}
          onSaved={loadSummary}
        />
      )}
    </PageContainer>
  );
};

export default AgentBalancesPage;
